/** HSL to #rrggbb — avoids depending on the browser to resolve colours. */
function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const saturation = Math.min(Math.max(s, 0), 100) / 100;
  const lightness = Math.min(Math.max(l, 0), 100) / 100;

  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const second = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const match = lightness - chroma / 2;

  let [r, g, b] = [0, 0, 0];
  if (hue < 60) [r, g, b] = [chroma, second, 0];
  else if (hue < 120) [r, g, b] = [second, chroma, 0];
  else if (hue < 180) [r, g, b] = [0, chroma, second];
  else if (hue < 240) [r, g, b] = [0, second, chroma];
  else if (hue < 300) [r, g, b] = [second, 0, chroma];
  else [r, g, b] = [chroma, 0, second];

  const channel = (value: number) =>
    Math.round((value + match) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/**
 * The personal companion's colours. Built from the same three-tone recipe as
 * an avatar, but from a hue the person picked rather than a hash, so the
 * character on the map and the one in the corner always match.
 */
export interface CompanionPalette {
  light: string;
  base: string;
  dark: string;
  /** Pale tint for the tummy and the glow around the antenna. */
  belly: string;
  lightHex: number;
  baseHex: number;
  darkHex: number;
  bellyHex: number;
}

export function companionPalette(hue: number): CompanionPalette {
  const light = hslToHex(hue - 12, 96, 76);
  const base = hslToHex(hue, 86, 62);
  const dark = hslToHex(hue + 12, 70, 42);
  const belly = hslToHex(hue - 6, 100, 86);
  const hex = (value: string) => Number.parseInt(value.slice(1), 16);
  return {
    light,
    base,
    dark,
    belly,
    lightHex: hex(light),
    baseHex: hex(base),
    darkHex: hex(dark),
    bellyHex: hex(belly),
  };
}

export const COMPANION_HUES = [222, 250, 276, 300, 330, 350, 12, 28, 160, 174, 190, 205] as const;
